import React, { useEffect, useMemo, useState } from 'react';
import { Drawer, DescriptionList } from '@wikifactory/voxel-ui';
import { OverflowTooltip } from 'components';
import wikifactoryNew from 'styles/themes/wikifactoryNew';
import { observer } from 'mobx-react';

import { formatDate } from 'utils/formatStrings';
import { useAppManager } from 'core/hooks';
import { ContributionSidebarConnManager } from './connManager';
import { FileHistory } from './FileHistory';

type FileSideBarProps = {
  open: boolean;
  isNotLocallyConnected?: boolean;
  project: any;
  file: DriveTableDataSource;
  onClosePanel: () => void;
  onRestoreFileToVersion: (
    file: DriveTableDataSource,
    contribution: any,
  ) => void;
};

export const FileSideBar = observer(
  ({
    project,
    file,
    open,
    onRestoreFileToVersion,
    onClosePanel,
    isNotLocallyConnected,
  }: FileSideBarProps) => {
    const [activeTabKey, setValue] = useState('Details');

    const projectId = project?.id;

    const contributionsConn = useAppManager(
      ContributionSidebarConnManager,
      null,
      {
        projectId,
        path: file?.id,
      },
      Boolean(projectId && file?.id),
      [projectId, file?.id],
    );

    useEffect(() => {
      if (contributionsConn) {
        contributionsConn.loadMore();
      }
    }, [contributionsConn]);

    const detailsItems = [
      {
        key: 'path',
        label: 'Path',
        value: <OverflowTooltip text={file?.id} />,
      },
      {
        key: 'lastModifiedAt',
        label: 'Last Modified at',
        value: file?.lastModified && formatDate(file.lastModified),
      },
      {
        key: 'version',
        label: 'Version',
        value: contributionsConn?.total,
      },
      {
        key: 'createdBy',
        label: 'Created By',
        value: file?.createdBy,
      },
      {
        key: 'createdAt',
        label: 'Created At',
        value: file?.createdAt && formatDate(file.createdAt),
      },
      // {
      //   key: 'maturity',
      //   label: 'File Maturity',
      //   value: file?.maturity,
      // },
      // {
      //   key: 'status',
      //   label: 'Status',
      //   value: file?.status,
      // },
    ];
    if (file?.lockedBy && file.lockedBy !== '—') {
      detailsItems.push({
        key: 'lockedBy',
        label: 'Locked by',
        value: file.lockedBy,
      });
    }

    const tabs = [
      {
        key: 'Details',
        content: (
          <>
            <DescriptionList
              items={detailsItems}
              valueClassName='overflow-hidden'
            />
            {/* <DescriptionList title='Metadata' items={metadataItems} /> */}
          </>
        ),
      },
      {
        key: 'History',
        content: (
          <FileHistory
            project={project}
            file={file}
            contributionsConn={contributionsConn}
            onClosePanel={onClosePanel}
            onRestoreFileToVersion={onRestoreFileToVersion}
            isNotLocallyConnected={isNotLocallyConnected}
          />
        ),
      },
    ];

    const themeFontFamily = wikifactoryNew.font.family.primary;

    const activeTabContent = useMemo(() => {
      return tabs.find(tab => tab.key === activeTabKey)?.content;
    }, [tabs, activeTabKey]);

    const defaultPadding = '1.5rem 2.5rem';

    return (
      <>
        <Drawer
          title={file?.filename}
          sx={{
            '& .MuiDrawer-paper': {
              width: '23.75rem',
              padding: defaultPadding,
              overflow: 'hidden',
            },
            zIndex: 1000000,
            fontFamily: themeFontFamily,
          }}
          style={{
            padding: defaultPadding,
            fontFamily: themeFontFamily,
          }}
          open={open}
          onClosePanel={onClosePanel}
        >
          <div className='flex items-center mb-6 justify-between font-helvetica'>
            {tabs.map(({ key }) => (
              <div
                onClick={() => {
                  setValue(key);
                }}
                className={`pb-1 cursor-pointer justify-center flex flex-grow ${
                  activeTabKey === key
                    ? ' font-extrabold border-b border-black'
                    : ' font-normal'
                }`}
                key={key}
              >
                {key}
              </div>
            ))}
          </div>
          <div className='flex flex-col gap-6 font-helvetica text-black overflow-scroll'>
            {activeTabContent}
          </div>
        </Drawer>
      </>
    );
  },
);
